import type { QuizExerciseConfig } from "@calega/content";
import type { ExerciseDefinition } from "../contract";

export interface QuizState {
  answers: Record<string, string[]>;
  submitted: boolean;
  correct: number;
  total: number;
}

export type QuizCommand =
  | { type: "answer"; questionId: string; optionId: string; selected: boolean }
  | { type: "submit" }
  | { type: "reset" };

export function quizAnswerIsCorrect(
  question: QuizExerciseConfig["questions"][number],
  selectedOptionIds: string[],
) {
  const selected = new Set(selectedOptionIds);
  return selected.size === question.correctOptionIds.length
    && question.correctOptionIds.every((optionId) => selected.has(optionId));
}

function scoreQuiz(config: QuizExerciseConfig, answers: Record<string, string[]>) {
  return config.questions.reduce(
    (total, question) => total + (quizAnswerIsCorrect(question, answers[question.id] ?? []) ? 1 : 0),
    0,
  );
}

export const quizExercise: ExerciseDefinition<QuizExerciseConfig, QuizState, QuizCommand> = {
  type: "quiz",
  engineVersion: 2,
  create(config) {
    return { answers: {}, submitted: false, correct: 0, total: config.questions.length };
  },
  reduce(config, state, command) {
    if (command.type === "reset") {
      return this.create(config);
    }
    if (command.type === "answer") {
      const question = config.questions.find((candidate) => candidate.id === command.questionId);
      if (state.submitted || !question || !question.options.some((option) => option.id === command.optionId)) {
        return state;
      }
      const current = state.answers[command.questionId] ?? [];
      const answers = question.correctOptionIds.length === 1
        ? command.selected ? [command.optionId] : []
        : command.selected
          ? [...new Set([...current, command.optionId])]
          : current.filter((optionId) => optionId !== command.optionId);
      return {
        ...state,
        answers: { ...state.answers, [command.questionId]: answers },
      };
    }
    return {
      ...state,
      submitted: true,
      correct: scoreQuiz(config, state.answers),
    };
  },
  snapshot(state) {
    return { exerciseType: this.type, engineVersion: this.engineVersion, state };
  },
  restore(snapshot) {
    if (snapshot.exerciseType !== this.type || snapshot.engineVersion !== this.engineVersion) {
      throw new Error("Der Übungs-Snapshot ist mit dieser Engine-Version nicht kompatibel.");
    }
    return structuredClone(snapshot.state);
  },
};
